import { useCallback, useEffect } from "react";
import { useIntl } from "react-intl";
import { isMobile } from "@rio-cloud/rio-uikit/utils/deviceUtils";
import { IChildForm } from "../../../";
import SearchAutocomplete from "../../../../../components/form/searchAutocomplete";
import useDealership from "../../../../../hooks/useDealership";
import { Dealership } from "../../../../../models/dealership";
import { fetchDealerships } from "../../../../../api/fetchDealerships";
import { getLastOccurrenceDispatch, isAssistanceOccurrenceDispatch, isCancelledOccurrenceDispatch } from "../../../../../hooks/useDispatch";

const DealershipChildForm = (props: IChildForm) => {
  const intl = useIntl();
  const { getDealershipByDN, dealership } = useDealership();

  const fetchDealershipsCallback = useCallback(
    (searchTerm: string) => fetchDealerships(searchTerm, searchTerm),
    []
  );

  useEffect(() => {
    if (props.occurrence?.dealershipDn) {
      getDealershipByDN(props.occurrence.dealershipDn);
    }
  }, [props.occurrence?.dealershipDn]);

  const isTower = props.persona === 'TOWER';
  const hasDnAlreadySet = Boolean(props.occurrence?.dealershipDn);

  // Desabilitar o campo de concessionária se o DN já estiver setado 
  // e o último acionamento for do tipo Socorro com status diferente de cancelado (task 37812)
  const lastOccurrenceDispatch = getLastOccurrenceDispatch(props.occurrence?.dispatches);
  const isLastDispatchAssistance = isAssistanceOccurrenceDispatch(lastOccurrenceDispatch?.occurrenceType);
  const isLastDispatchCancelled = isCancelledOccurrenceDispatch(lastOccurrenceDispatch);

  const isDisabled = (hasDnAlreadySet && !isTower) || (isLastDispatchAssistance && !isLastDispatchCancelled);

  return (
    <div className="w-full my-6 p-6 pb-0 border rounded">
      <div id="dealershipDn-container" className="flex flex-col md:flex-row md:justify-between w-full gap-4 md:gap-6">
        {!props.hiddenFields.includes("dealershipId") &&
          !props.occurrence?.scheduleNumber && (
            <div
              className={`form-group form-group-lg w-full ${
                props.hasError && props.requiredFields.includes("dealershipDn") && !props.occurrence?.dealershipDn
                  ? "has-error"
                  : ""
              }`}
            >
              <label htmlFor="dealershipId" className="truncate">
                {intl.formatMessage({ id: "intl-msg:kanban.form.dealershipChildForm.searchDealership" })}
              </label>
              <SearchAutocomplete
                id="dealershipId"
                placeholder={intl.formatMessage({ id: "intl-msg:kanban.form.dealershipChildForm.searchDealershipPlaceholder" })}
                fetchOptions={fetchDealershipsCallback}
                optionLabel={{
                  name: intl.formatMessage({ id: "intl-msg:kanban.form.dealershipChildForm.optionName" }),
                  address: intl.formatMessage({ id: "intl-msg:kanban.form.dealershipChildForm.optionAddress" }),
                  neighborhood: intl.formatMessage({ id: "intl-msg:kanban.form.dealershipChildForm.optionNeighborhood" }),
                  city: intl.formatMessage({ id: "intl-msg:kanban.form.dealershipChildForm.optionCity" }),
                  state: intl.formatMessage({ id: "intl-msg:kanban.form.dealershipChildForm.optionState" }),
                }}
                onSelectedOption={(dealership: Dealership) => {
                  props.setOccurrenceValue("dealershipDn", dealership.id);
                  props.setOccurrenceValue("dealershipName", dealership.name);
                  props.setOccurrenceValue("dealershipRegional", dealership.regional ?? null);
                  props.setOccurrenceValue("dealershipCell", dealership.cell ?? null);
                  props.setOccurrenceValue("dealershipUniqueId", dealership.cnpj ?? null);
                  props.setOccurrenceValue("dealershipLocation", dealership.address ?? null);
                  props.setOccurrenceValue("dealershipRepresentative", dealership.representative ?? null);
                }}
                disabled={isDisabled}
              />
            </div>
          )}
      </div>

      <div
        className={`flex justify-between w-full gap-6 ${
          isMobile() ? "flex-wrap" : ""
        }`}
      >
        {!props.hiddenFields.includes("dealershipName") && (
          <div
            className={`form-group form-group-lg w-full ${
              props.hasError &&
              props.requiredFields.includes("dealershipName") &&
              !props.occurrence?.dealershipName
                ? "has-error"
                : ""
            }`}
          >
            <label htmlFor="dealershipName" className="truncate">
              {intl.formatMessage({ id: "intl-msg:kanban.form.dealershipChildForm.dealershipName" })}
              {props.requiredFields.includes("dealershipName") ? " *" : ""}
            </label>
            <input
              type="text"
              className="form-control"
              id="dealershipName"
              placeholder={intl.formatMessage({ id: "intl-msg:kanban.form.dealershipChildForm.dealershipName" })}
              disabled={true}
              value={props.occurrence?.dealershipName ?? dealership?.name ?? ""}
              maxLength={100}
            />
          </div>
        )}

        {!props.hiddenFields.includes("dealershipDn") && (
          <div
            className={`form-group form-group-lg w-full ${
              props.hasError &&
              props.requiredFields.includes("dealershipDn") &&
              !props.occurrence?.dealershipDn
                ? "has-error"
                : ""
            }`}
          >
            <label htmlFor="dn" className="truncate">
              DN{props.requiredFields.includes("dealershipDn") ? " *" : ""}
            </label>
            <input
              type="text"
              className="form-control"
              id="dn"
              placeholder="DN"
              disabled={true}
              value={props.occurrence?.dealershipDn ?? dealership?.id ?? ""}
              maxLength={100}
            />
          </div>
        )}
      </div>

      <div
        className={`flex justify-between w-full gap-6 ${
          isMobile() ? "flex-wrap" : ""
        }`}
      >
        {!props.hiddenFields.includes("dealershipUniqueId") && (
          <div
            className={`form-group form-group-lg w-full ${
              props.hasError &&
              props.requiredFields.includes("dealershipUniqueId") &&
              !props.occurrence?.dealershipUniqueId
                ? "has-error"
                : ""
            }`}
          >
            <label htmlFor="cnpj" className="truncate">
              CNPJ
              {props.requiredFields.includes("dealershipUniqueId") ? " *" : ""}
            </label>
            <input
              type="text"
              className="form-control"
              id="cnpj"
              placeholder="CNPJ"
              disabled={true}
              value={props.occurrence?.dealershipUniqueId ?? dealership?.cnpj ?? ""}
              maxLength={14}
            />
          </div>
        )}

        {!props.hiddenFields.includes("dealershipLocation") && (
          <div
            className={`form-group form-group-lg w-full ${
              props.hasError &&
              props.requiredFields.includes("dealershipLocation") &&
              !props.occurrence?.dealershipLocation
                ? "has-error"
                : ""
            }`}
          >
            <label htmlFor="location" className="truncate">
              {intl.formatMessage({ id: "intl-msg:kanban.form.dealershipChildForm.location" })}
              {props.requiredFields.includes("dealershipLocation") ? " *" : ""}
            </label>
            <input
              type="text"
              className="form-control"
              id="location"
              placeholder={intl.formatMessage({ id: "intl-msg:kanban.form.dealershipChildForm.location" })}
              disabled={true}
              value={props.occurrence?.dealershipLocation ?? dealership?.address ?? ""}
              maxLength={100}
            />
          </div>
        )}

        {!props.hiddenFields.includes("dealershipRepresentative") && (
          <div
            className={`form-group form-group-lg w-full ${
              props.hasError &&
              props.requiredFields.includes("dealershipRepresentative") &&
              !props.occurrence?.dealershipRepresentative
                ? "has-error"
                : ""
            }`}
          >
            <label htmlFor="representative" className="truncate">
              {intl.formatMessage({ id: "intl-msg:kanban.form.dealershipChildForm.representative" })}
              {props.requiredFields.includes("dealershipRepresentative")
                ? " *"
                : ""}
            </label>
            <input
              type="text"
              className="form-control"
              id="representative"
              placeholder={intl.formatMessage({ id: "intl-msg:kanban.form.dealershipChildForm.representative" })}
              disabled={true}
              value={props.occurrence?.dealershipRepresentative ?? dealership?.representative ?? ""}
              maxLength={100}
            />
          </div>
        )}
      </div>

      <div
        className={`flex justify-between w-full gap-6 ${
          isMobile() ? "flex-wrap" : ""
        }`}
      >
        {!props.hiddenFields.includes("dealershipRegional") && (
          <div
            className={`form-group form-group-lg w-full ${
              props.hasError &&
              props.requiredFields.includes("dealershipRegional") &&
              !props.occurrence?.dealershipRegional
                ? "has-error"
                : ""
            }`}
          >
            <label htmlFor="regional" className="truncate">
              {intl.formatMessage({ id: "intl-msg:kanban.form.dealershipChildForm.regional" })}
              {props.requiredFields.includes("dealershipRegional") ? " *" : ""}
            </label>
            <input
              type="text"
              className="form-control"
              id="regional"
              placeholder={intl.formatMessage({ id: "intl-msg:kanban.form.dealershipChildForm.regional" })}
              disabled={true}
              value={props.occurrence?.dealershipRegional ?? dealership?.regional ?? ""}
              maxLength={100}
            />
          </div>
        )}

        {!props.hiddenFields.includes("dealershipPhone") && (
          <div
            className={`form-group form-group-lg w-full ${
              props.hasError &&
              props.requiredFields.includes("dealershipPhone") &&
              !props.occurrence?.dealershipPhone
                ? "has-error"
                : ""
            }`}
          >
            <label htmlFor="cellNumber" className="truncate">
              {intl.formatMessage({ id: "intl-msg:kanban.form.dealershipChildForm.cellNumber" })}
              {props.requiredFields.includes("dealershipPhone") ? " *" : ""}
            </label>
            <input
              type="text"
              className="form-control"
              id="cellNumber"
              placeholder={intl.formatMessage({ id: "intl-msg:kanban.form.dealershipChildForm.cellNumber" })}
              disabled={true}
              value={props.occurrence?.dealershipPhone ?? dealership?.cell ?? ""}
              maxLength={15}
            />
          </div>
        )}

        {/* {!props.hiddenFields.includes("dealershipDemarcatedArea") && (
          <div
            className={`form-group form-group-lg w-full ${
              props.hasError &&
              props.requiredFields.includes("dealershipDemarcatedArea") &&
              !props.occurrence?.dealershipDemarcatedArea
                ? "has-error"
                : ""
            }`}
          >
            <label htmlFor="demarcatedArea" className="truncate">
              Área demarcada
              {props.requiredFields.includes("dealershipDemarcatedArea")
                ? " *"
                : ""}
            </label>
            <input
              type="text"
              className="form-control"
              id="demarcatedArea"
              placeholder="Área demarcada"
              disabled={
                props.disabledAllFields ||
                props.disabledFields.includes("dealershipDemarcatedArea")
              }
              value={props.occurrence?.dealershipDemarcatedArea}
              onChange={(e) =>
                props.occurrence &&
                props.setOccurrenceValue(
                  "dealershipDemarcatedArea",
                  e.target.value
                )
              }
              maxLength={100}
            />
          </div>
        )} */}
      </div>
    </div>
  );
};

export default DealershipChildForm;
